// Vaucherlar — xsib-core `subscriber` moduli bilan haqiqiy kontrakt.
// Manba: `subscriber/internal/VoucherController.java`, `subscriber/api/Voucher*.java`.
//
// ⚠️ HUQUQLAR — maxsus `voucher.*` kod YO'Q, mavjudlari qarzga olingan
// (`VoucherController` javadoc'i, har birida sabab bor):
//   · partiya yaratish / kodlarni ko'rish  → `subscriber.create`
//     (vaucher aktivlashtirilganda abonentga aylanadi, kod esa PUL QIYMATI —
//     uni ko'rish huquqi partiyani CHIQARISH huquqidan past bo'lmasin)
//   · partiya ro'yxati                     → `subscriber.read`
//   · partiyani bekor qilish               → `subscriber.delete` + `plan.create`
//   · BITTA kartani bekor qilish           → `subscriber.delete` YOLG'IZ
//     (partiyada `plan.create` ham bor, bu yerda ATAYLAB yo'q: partiya bir
//      amalda yuzlab kartani kuydiradi, bitta karta esa kundalik mijoz ishi —
//      "kartamni yo'qotdim, bloklang". `VoucherController.voidVoucher`)
//   · "sotildi" belgisi va uni QAYTARISH   → `payment.create` (pul hodisasi;
//     amalni bekor qilish huquqi uni bajarish huquqidan yuqori bo'lsa, agent
//     o'z xatosini tuzatolmaydi — `VoucherController.unsell`)
//
// ⚠️ Kod — BIR MARTALIK SIR: shu fayldagi `fetchVoucherCodes` natijasi
// React Query keshiga SAQLANMAYDI (oddiy `async function`, `useQuery` emas)
// — chaqiruvchi (`VoucherCodesDrawer`) uni faqat lokal komponent holatida
// tutadi va oyna yopilganda yo'qoladi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, moneySchema, toMoney, uuidSchema } from '@/shared/api/schemas'
import {
  listParams,
  serverPageSchema,
  toPageResult,
  type SortFieldMap,
} from '@/shared/api/serverPage'
import type { Money } from '@/shared/lib/money'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

export const VOUCHER_STATES = ['NEW', 'SOLD', 'ACTIVATED', 'EXPIRED', 'VOIDED'] as const
export type VoucherState = (typeof VOUCHER_STATES)[number]

/** Partiya turi (ADR-056): aralash kod yoki raqamli login va parol */
export const CREDENTIAL_MODES = ['CODE', 'LOGIN_PASSWORD'] as const
export type CredentialMode = (typeof CREDENTIAL_MODES)[number]

export const LOGIN_DIGITS = { min: 6, max: 12 } as const
export const PASSWORD_DIGITS = { min: 4, max: 12 } as const

/** Bitta partiyadagi kartalar soni login xonasidagi bo'sh joyning 1 foizidan oshmaydi (`VoucherServiceImpl`) */
export function loginBatchLimit(loginDigits: number): number {
  return 9 * 10 ** (loginDigits - 3)
}

// --- Partiya ------------------------------------------------------------------

const voucherBatchSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema,
  batchNo: z.number(),
  note: z.string().nullish(),
  planId: uuidSchema,
  agentId: uuidSchema.nullish(),
  /**
   * Partiya QAYSI ombor/filialda yotibdi.
   *
   * ⚠️ `agentId` bilan BIR VAQTDA to'lmaydi — karta yo agentning
   * qo'lida, yo omborda (bazadagi `voucher_batch_single_holder`).
   * Ikkalasi ham bo'sh = "operatorniki, hali biriktirilmagan".
   *
   * `.nullish()` — eski backend bu maydonni umuman yubormaydi.
   */
  nodeId: uuidSchema.nullish(),
  codePrefix: z.string().nullish(),
  codeLength: z.number(),
  quantity: z.number(),
  price: moneySchema,
  /** Karta SOTILMASA qachon kuyadi — `Voucher.expiresAt` (aktivlashtirilgandan keyingi muddat) BILAN CHALKASHTIRMASLIK */
  validUntil: instantSchema.nullish(),
  createdBy: z.string().nullish(),
  createdAt: instantSchema,
  vispId: uuidSchema.nullish(),
  instructions: z.string().nullish(),
  /** Faqat ro'yxatda keladi; yaratish va ko'chirish javobida yo'q */
  visp: z
    .object({ id: uuidSchema, name: z.string(), centerId: uuidSchema, centerName: z.string() })
    .nullish(),
  /**
   * Kartalarning holat bo'yicha sanog'i.
   *
   * ⚠️ `.nullish()` — eski backend bilan ishlaganda maydon umuman
   * bo'lmaydi va u holda ustun "—" ko'rsatadi, panel esa yiqilmaydi.
   * Backend uni to'liq to'ldirib yuboradi (nolli holatlar ham bor),
   * ya'ni "kalit yo'q" va "nol" chalkashmaydi (`VoucherBatchView`).
   */
  counts: z.record(z.enum(VOUCHER_STATES), z.number()).nullish(),
  /** Yaratish va bitta partiya javobida keladi; ro'yxatda hozircha yo'q */
  credentialMode: z.enum(CREDENTIAL_MODES).nullish(),
  loginDigits: z.number().nullish(),
  passwordDigits: z.number().nullish(),
  printQr: z.boolean().nullish(),
})

export interface VoucherBatch {
  id: string
  /** Partiyaning inson o'qiydigan raqami — kartada `#<batchNo>-<serialNo>` */
  batchNo: number
  /** Operator izohi (filial nomi va h.k.), har kartaga chop etiladi */
  note: string | null
  planId: string
  agentId: string | null
  /** Ombor/filial. `agentId` bilan birga to'lmaydi (sxema izohi). */
  nodeId: string | null
  codePrefix: string | null
  codeLength: number
  quantity: number
  price: Money
  validUntil: string | null
  createdBy: string | null
  createdAt: string
  /** Karta qaysi VISP uchun; ishlatgan hotspot abonenti shu VISPga o'tadi */
  vispId: string | null
  /** Partiya yo'riqnomasi; `null` — tashkilotning umumiy matni chiqadi */
  instructions: string | null
  visp: { id: string; name: string; centerId: string; centerName: string } | null
  /** Holat -> soni. `null` — backend bermadi (eski versiya). */
  counts: Partial<Record<VoucherState, number>> | null
  /** `null` — backend bu javobda turini bermadi */
  credentialMode: CredentialMode | null
  loginDigits: number | null
  passwordDigits: number | null
  printQr: boolean | null
}

function toBatch(raw: z.infer<typeof voucherBatchSchema>): VoucherBatch {
  return {
    id: raw.id,
    batchNo: raw.batchNo,
    note: raw.note ?? null,
    planId: raw.planId,
    agentId: raw.agentId ?? null,
    nodeId: raw.nodeId ?? null,
    codePrefix: raw.codePrefix ?? null,
    codeLength: raw.codeLength,
    quantity: raw.quantity,
    price: toMoney(raw.price),
    validUntil: raw.validUntil ?? null,
    createdBy: raw.createdBy ?? null,
    createdAt: raw.createdAt,
    vispId: raw.vispId ?? null,
    instructions: raw.instructions ?? null,
    visp: raw.visp ?? null,
    counts: raw.counts ?? null,
    credentialMode: raw.credentialMode ?? null,
    loginDigits: raw.loginDigits ?? null,
    passwordDigits: raw.passwordDigits ?? null,
    printQr: raw.printQr ?? null,
  }
}

export interface VoucherBatchDraft {
  planId: string
  agentId?: string | null
  /** Bo'sh — prefikssiz */
  codePrefix?: string | null
  codeLength: number
  quantity: number
  /** `null` — karta sotilmasa ham kuymaydi */
  validUntil?: string | null
  /** Bo'sh — izohsiz */
  note?: string | null
  vispId?: string | null
  instructions?: string | null
  credentialMode: CredentialMode
  /** Faqat `LOGIN_PASSWORD` da; `codeLength` ham shunga teng yuboriladi */
  loginDigits?: number | null
  passwordDigits?: number | null
  printQr: boolean
}

export const VOUCHER_NOTE_MAX = 60
export const VOUCHER_INSTRUCTIONS_MAX = 160

export async function generateVoucherBatch(draft: VoucherBatchDraft): Promise<VoucherBatch> {
  const credentials = draft.credentialMode === 'LOGIN_PASSWORD'
  const response = await api.post<unknown>('/api/vouchers/batches', {
    planId: draft.planId,
    agentId: draft.agentId ?? null,
    codePrefix: credentials ? '' : draft.codePrefix?.trim() || '',
    codeLength: draft.codeLength,
    quantity: draft.quantity,
    validUntil: draft.validUntil ?? null,
    note: draft.note?.trim() || null,
    vispId: draft.vispId ?? null,
    instructions: draft.instructions?.trim() || null,
    credentialMode: draft.credentialMode,
    loginDigits: credentials ? (draft.loginDigits ?? null) : null,
    passwordDigits: credentials ? (draft.passwordDigits ?? null) : null,
    printQr: draft.printQr,
  })
  return toBatch(voucherBatchSchema.parse(response.data))
}

/**
 * Ustun kaliti → `GET /api/vouchers/batches` ning `sortField` enumi.
 *
 * ⚠️ `limit` parametri OLIB TASHLANDI (2026-08-20): endpoint sahifalanadi
 * va eski parametr e'tiborsiz qoladi yoki 400 beradi. U bilan birga
 * «ro'yxat kesildi» banneri ham keraksiz bo'ldi — sahifa tugmalari bor.
 *
 * ⚠️ `plan` ustuni saralanmaydi: server paket NOMINI bilmaydi (javobda
 * faqat `planId`), panel esa nomni katalogdan qo'shadi. `PLAN` enumi ham
 * yo'q — noto'g'ri qiymat 400 berardi.
 */
const VOUCHER_BATCH_SORT_FIELDS: SortFieldMap = {
  batchNo: 'BATCH_NO',
  codePrefix: 'CODE_PREFIX',
  quantity: 'QUANTITY',
  validUntil: 'VALID_UNTIL',
  createdAt: 'CREATED_AT',
}

/** Bir SAHIFA partiya — qidiruv, saralash va sahifalash SERVERDA */
export async function fetchVoucherBatchPage(
  state: TableState,
  signal: AbortSignal,
): Promise<PageResult<VoucherBatch>> {
  const vispId = state.filters['visp']?.[0]
  const centerId = state.filters['center']?.[0]
  const response = await api.get<unknown>('/api/vouchers/batches', {
    signal,
    params: {
      ...listParams(state, { sortFields: VOUCHER_BATCH_SORT_FIELDS }),
      ...(vispId ? { vispId } : {}),
      ...(centerId ? { centerId } : {}),
    },
  })
  const parsed = serverPageSchema(voucherBatchSchema).parse(response.data)
  return toPageResult(parsed, parsed.rows.map(toBatch))
}

/**
 * Butun partiyani bekor qiladi — qaytarib bo'lmaydi (`subscriber.delete`).
 *
 * ⚠️ Javobdagi `voided` — HAQIQATDA kuygan kartalar soni va u partiya
 * HAJMIDAN kam bo'lishi mumkin. Chaqiruvchi uni ko'rsatishi SHART:
 * "25 ta karta bekor qilindi" degan taxmin ulardan 7 tasi allaqachon
 * aktivlashtirilgan bo'lgan holatda yolg'on bo'lardi.
 */
export async function voidVoucherBatch(id: string, reason?: string): Promise<number> {
  const response = await api.delete<unknown>(`/api/vouchers/batches/${id}`, {
    params: reason ? { reason } : {},
  })
  return z.object({ voided: z.number() }).parse(response.data).voided
}

export interface VoucherRangeVoidResult {
  fromSerial: number
  toSerial: number
  /** Haqiqatda bekor qilingan kartalar */
  voided: number
  /** Oraliqda bo'lib, tegilmaganlari: faollashgan, muddati o'tgan yoki allaqachon bekor qilingan */
  skipped: number
}

/** Partiya ichidagi seriya oralig'ini bekor qiladi — faqat `NEW`/`SOLD` kartalar (ADR-056) */
export async function voidVoucherRange(
  batchId: string,
  range: { fromSerial: number; toSerial: number },
  reason: string,
): Promise<VoucherRangeVoidResult> {
  const response = await api.post<unknown>(`/api/vouchers/batches/${batchId}/void-range`, { ...range, reason })
  return z
    .object({ fromSerial: z.number(), toSerial: z.number(), voided: z.number(), skipped: z.number() })
    .parse(response.data)
}

/**
 * Bekor qilinishi MUMKIN bo'lgan kartalar soni — `quantity` EMAS.
 *
 * ⚠️ Faqat `NEW` va `SOLD` bekor qilinadi; `ACTIVATED`, `EXPIRED` va
 * allaqachon `VOIDED` bo'lganlariga amal TEGMAYDI (`voidVoucher`
 * javadoc'i — ular 409 beradi). Tasdiq oynasida `quantity` ni
 * ko'rsatish operatorga haqiqiydan KO'P karta va'da qilardi.
 *
 * @returns `null` — backend holat sanog'ini bermadi
 *          (`VoucherBatch.counts`), ya'ni son NOMA'LUM. Bu holatda
 *          `quantity` ga qaytish yaramaydi: u yana o'sha yolg'on son
 *          bo'lardi, faqat endi "hisoblangan" qiyofada.
 */
export function voidableCount(batch: VoucherBatch): number | null {
  if (batch.counts === null) return null
  return (batch.counts.NEW ?? 0) + (batch.counts.SOLD ?? 0)
}

/**
 * Partiyani ombor/filialga biriktiradi, agentga beradi yoki QAYTARADI.
 *
 * ⚠️ BITTA amal, uch vaziyat: nishonning TURI qaysi biri ekanini
 * belgilaydi (backend `VoucherService.transferBatch`) —
 * `holdsStock` tugun = omborga (yoki qaytarish), `settlementParty`
 * tugun = agentga berish.
 *
 * ⚠️ `targetNodeId` MAJBURIY va `null` qabul qilinmaydi. "Bo'sh qiymat
 * = biriktirishni olib tashlash" semantikasi `QARORLAR.md` §5.3.3 dagi
 * nuqsonning o'zi bo'lardi (u yerda maydonsiz kelgan so'rov abonentni
 * agentidan JIMGINA uzib qo'yardi). Qaytarish ham manzilli amal —
 * "ombordan chiqdi, omborga qaytdi".
 *
 * Xatolar:
 *   · **404** — partiya yoki nishon topilmadi YOKI doiradan tashqarida
 *     (403 EMAS: u "bunday narsa bor" degan ma'lumotni oshkor qilardi);
 *   · **409** `VOUCHER_TRANSFER_SOLD` — partiyada allaqachon sotilgan
 *     yoki ishlatilgan karta bor. Sotuv kimga yozilishi partiyaning
 *     egasidan chiqadi, ya'ni ko'chirish eski agentning savdosini
 *     jimgina yangisiga o'tkazardi.
 */
export async function transferVoucherBatch(
  id: string,
  targetNodeId: string,
  reason?: string,
): Promise<VoucherBatch> {
  const response = await api.post<unknown>(`/api/vouchers/batches/${id}/transfer`, {
    targetNodeId,
    reason: reason ?? null,
  })
  return toBatch(voucherBatchSchema.parse(response.data))
}

/**
 * Muddati o'tib kuygan kartalarning NOMINAL zarari — minor birlikda.
 *
 * ⚠️ Sanoq yolg'iz yetmaydi: 100 ta arzon va 2 ta qimmat karta bir xil
 * sonni beradi, zarari esa butunlay boshqa (`QARORLAR.md` §20.8 —
 * "muddati o'tganini hisobdan chiqarish (moliyaviy zarar)").
 *
 * ⚠️ Bu KUTILAYOTGAN daromadning yo'qolishi, kassadan chiqqan pul EMAS:
 * vaucher sotuvi ledger'ga umuman yozilmaydi. Shuning uchun UI'da u
 * "xarajat" deb emas, "kuygan qiymat" deb ko'rsatiladi.
 *
 * @returns `null` — backend holat sanog'ini bermadi, ya'ni son NOMA'LUM.
 *          Nolga qaytish yaramaydi: u "zarar yo'q" degan YOLG'ON javob
 *          bo'lardi (`voidableCount` bilan bir xil qoida).
 */
export function expiredLossMinor(batch: VoucherBatch): number | null {
  if (batch.counts === null) return null
  return (batch.counts.EXPIRED ?? 0) * batch.price.amount
}

// --- Kartalar -------------------------------------------------------------------

const voucherSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema,
  batchId: uuidSchema,
  serialNo: z.number().nullish(),
  code: z.string(),
  state: z.enum(VOUCHER_STATES),
  subscriberId: uuidSchema.nullish(),
  grantId: uuidSchema.nullish(),
  soldAt: instantSchema.nullish(),
  activatedAt: instantSchema.nullish(),
  expiresAt: instantSchema.nullish(),
  /*
    ⚠️ KIM SOTDI / KIM ISHLATDI — BAZAGA KEYIN QO'SHILADIGAN USTUNLAR.

    Eski qatorlarda ular BO'SH bo'ladi va bu «hech kim ishlatmagan»
    degani EMAS — «o'sha paytda ustun yo'q edi» degani. Farqni ekranda
    `voucherActor.ts` ajratadi; bu yerda faqat shakl.

    ⚠️ `.nullish()` — backend hozircha bu maydonlarni UMUMAN yubormaydi
    (`null-contract.mjs`: `non_null` ostida javobga maydon yozilmaydi,
    `null` kelmaydi). `.nullable()` bo'lsa butun ro'yxat `ZodError`
    bilan o'lardi.
  */
  soldBy: uuidSchema.nullish(),
  soldByName: z.string().nullish(),
  activatedBy: uuidSchema.nullish(),
  activatedByName: z.string().nullish(),
  createdAt: instantSchema,
})

export interface Voucher {
  id: string
  batchId: string
  /** Partiya ichidagi tartib raqami 1..quantity; `null` — eski backend bermadi */
  serialNo: number | null
  code: string
  state: VoucherState
  subscriberId: string | null
  soldAt: string | null
  activatedAt: string | null
  /** Aktivlashtirilgandan KEYIN qancha amal qiladi — `VoucherBatch.validUntil` EMAS */
  expiresAt: string | null
  /** Kartani «sotilgan» deb belgilagan aktyor. `null` — yozilmagan (sxema izohi). */
  soldBy: string | null
  soldByName: string | null
  /** Kartani ISHLATGAN aktyor. `null` — yozilmagan, «ishlatilmagan» EMAS. */
  activatedBy: string | null
  activatedByName: string | null
}

function toVoucher(raw: z.infer<typeof voucherSchema>): Voucher {
  return {
    id: raw.id,
    batchId: raw.batchId,
    serialNo: raw.serialNo ?? null,
    code: raw.code,
    state: raw.state,
    subscriberId: raw.subscriberId ?? null,
    soldAt: raw.soldAt ?? null,
    activatedAt: raw.activatedAt ?? null,
    expiresAt: raw.expiresAt ?? null,
    soldBy: raw.soldBy ?? null,
    soldByName: raw.soldByName ?? null,
    activatedBy: raw.activatedBy ?? null,
    activatedByName: raw.activatedByName ?? null,
  }
}

/**
 * Partiyadagi kartalar — KODLARNI OCHIB BERADI (`subscriber.create` talab
 * qilinadi, `subscriber.read` YETARLI EMAS — `VoucherController.codes`
 * javadoc'idagi sabab: kod pul qiymatiga teng bearer-token).
 */
export async function fetchVoucherCodes(batchId: string, limit = 1000): Promise<Voucher[]> {
  const response = await api.get<unknown>(`/api/vouchers/batches/${batchId}/codes`, {
    params: { limit },
  })
  return z.array(voucherSchema).parse(response.data).map(toVoucher)
}

const printSheetSchema = z.object({
  batchId: uuidSchema,
  batchNo: z.number(),
  planId: uuidSchema,
  note: z.string().nullish(),
  quantity: z.number(),
  price: moneySchema,
  validUntil: instantSchema.nullish(),
  portalUrl: z.string().nullish(),
  cards: z.array(
    z.object({
      serialNo: z.number(),
      code: z.string(),
      password: z.string().nullish(),
      qrToken: z.string().nullish(),
    }),
  ),
  skipped: z.number(),
  branchName: z.string().nullish(),
  companyName: z.string().nullish(),
  logoUrl: z.string().nullish(),
  instructions: z.string().nullish(),
  credentialMode: z.enum(CREDENTIAL_MODES).nullish(),
  printQr: z.boolean().nullish(),
})

export interface VoucherPrintSheetData {
  batchNo: number
  planId: string
  note: string | null
  quantity: number
  price: Money
  validUntil: string | null
  /** `null` — muhitda portal manzili sozlanmagan, QR faqat kodni saqlaydi */
  portalUrl: string | null
  /**
   * `password` — faqat login va parolli kartada (`code` shunda login).
   * `qrToken` — QR uchun alohida token; `null` — eski karta, QR kodni o'zi saqlaydi.
   */
  cards: { serialNo: number; code: string; password: string | null; qrToken: string | null }[]
  /** Tanlovda bor, lekin ishlatilgan/kuygan/bekor qilingani uchun chiqmaydigan kartalar */
  skipped: number
  /** Partiya ushlovchisi turgan filial; `null` — filialga bog'lanmagan */
  branchName: string | null
  /** Kompaniya nomi (brend sozlamasi, bo'lmasa tashkilot nomi) — kartada chiqaruvchi qatorida */
  companyName: string | null
  /** Tashkilot logotipi (ochiq havola); `null` — yuklanmagan */
  logoUrl: string | null
  /** Partiya yoki tashkilot yo'riqnomasi; `null` — kartada umumiy maslahat chiqadi */
  instructions: string | null
  credentialMode: CredentialMode
  /** `false` — partiya QR'siz chop etiladi */
  printQr: boolean
}

/**
 * Chop etish varag'i — KODLARNI ochadi, har so'rov serverda
 * `VOUCHER_BATCH_PRINT` auditini qoldiradi. Keshga saqlanmaydi.
 */
export async function fetchVoucherPrintSheet(
  batchId: string,
  range: { from: number; to: number },
): Promise<VoucherPrintSheetData> {
  const response = await api.get<unknown>(`/api/vouchers/batches/${batchId}/print`, { params: range })
  const raw = printSheetSchema.parse(response.data)
  return {
    batchNo: raw.batchNo,
    planId: raw.planId,
    note: raw.note ?? null,
    quantity: raw.quantity,
    price: toMoney(raw.price),
    validUntil: raw.validUntil ?? null,
    portalUrl: raw.portalUrl ?? null,
    cards: raw.cards.map((card) => ({
      serialNo: card.serialNo,
      code: card.code,
      password: card.password ?? null,
      qrToken: card.qrToken ?? null,
    })),
    skipped: raw.skipped,
    branchName: raw.branchName ?? null,
    companyName: raw.companyName ?? null,
    logoUrl: raw.logoUrl ?? null,
    instructions: raw.instructions ?? null,
    credentialMode: raw.credentialMode ?? 'CODE',
    printQr: raw.printQr ?? true,
  }
}

const cardViewSchema = z.object({
  voucherId: uuidSchema,
  batchId: uuidSchema,
  batchNo: z.number(),
  serialNo: z.number(),
  planId: uuidSchema,
  state: z.enum(VOUCHER_STATES),
  soldAt: instantSchema.nullish(),
  soldByName: z.string().nullish(),
  activatedAt: instantSchema.nullish(),
  activatedByName: z.string().nullish(),
  subscriberLogin: z.string().nullish(),
  expiresAt: instantSchema.nullish(),
})

export type VoucherCardView = z.infer<typeof cardViewSchema>

/** Karta `#<partiya>-<tartib raqami>` bo'yicha — KODSIZ (`subscriber.read`) */
export async function fetchVoucherCard(batchNo: number, serialNo: number): Promise<VoucherCardView> {
  const response = await api.get<unknown>(`/api/vouchers/batches/by-number/${batchNo}/cards/${serialNo}`)
  return cardViewSchema.parse(response.data)
}

/**
 * Kartani ISHLATISH — kod abonent paketiga aylanadi.
 *
 * <h2>⚠️ Nega bu panelda umuman paydo bo'ldi</h2>
 * Backendda `POST /api/vouchers/activate` ANCHADAN BERI bor, mehmon
 * portalida ham o'z yo'li bor (`portal` → `VoucherStep` →
 * `guest-voucher`). Panelda esa uni chaqiradigan BIRORTA tugma yo'q edi —
 * ya'ni operator kartani chiqarardi, chop etardi, sotardi va uni
 * ISHLATA OLMASDI. Tashqi tekshiruvchi shuni topdi: «Vaucherlarni
 * ishlatib bo'lmaydi».
 *
 * Bu ayniqsa PPPoE kartalarida muhim: mehmon portali faqat hotspot
 * zonasida ishlaydi, ya'ni portalsiz sotilgan kartaning boshqa yo'li
 * umuman yo'q edi.
 *
 * <h2>⚠️ HUQUQ QOIDASI SHU YERDA O'ZGARMAYDI</h2>
 * Endpoint `subscriber.create` talab qiladi va bu qaror avvalgidek
 * qoladi (`VoucherController.activate`). O'lchandi (Staging,
 * 2026-08-16, `identity.role_permission`): `SUPERADMIN`, `ADMIN`,
 * `MODERATOR` va `RESELLER` da bu huquq BOR, `AGENT` da — YO'Q. Ya'ni
 * bu tugma yangi hech kimga yangi imkoniyat bermaydi: u faqat
 * ALLAQACHON ruxsat etilgan amalga yetadigan yo'lni ochadi.
 *
 * @param login mavjud abonent logini — bo'lsa unga bog'lanadi, bo'lmasa
 *              YANGI abonent yaratiladi (karta — abonent EMAS,
 *              `docs/01-DOMAIN-MODEL.md`)
 */
export async function activateVoucher(code: string, login: string): Promise<Voucher> {
  const response = await api.post<unknown>('/api/vouchers/activate', {
    code: code.trim().toUpperCase(),
    login: login.trim(),
  })
  return toVoucher(voucherSchema.parse(response.data))
}

/** "Sotildi" belgisi — pul hodisasi (`payment.create`) */
export async function markVoucherSold(id: string): Promise<Voucher> {
  const response = await api.post<unknown>(`/api/vouchers/${id}/sold`)
  return toVoucher(voucherSchema.parse(response.data))
}

/**
 * Sotuvni QAYTARADI: `SOLD` → `NEW` (`payment.create`).
 *
 * ⚠️ `POST`, `DELETE` emas — hech narsa o'chmaydi, karta yana sotiladigan
 * holatga tushadi va kodi TIRIK sirligicha qoladi.
 *
 * Karta `SOLD` bo'lmasa backend **409** beradi
 * (`VOUCHER_UNSELL_<HOLAT>`) — ro'yxat eskirgan yoki boshqa operator
 * ulgurgan holat.
 */
export async function unsellVoucher(id: string): Promise<Voucher> {
  const response = await api.post<unknown>(`/api/vouchers/${id}/unsell`)
  return toVoucher(voucherSchema.parse(response.data))
}

/**
 * Sabab maydonining chegarasi — partiya uchun ham, bitta karta uchun ham
 * BITTA joyda.
 *
 * ⚠️ Backend uzunlikni TEKSHIRMAYDI: sabab audit tafsilotiga (`reason`)
 * yoziladi va u yerda cheklov yo'q. Ya'ni bu chegara faqat UI'niki, va
 * aynan shuning uchun bir joyda turishi kerak — ikki modalda ikki xil
 * son bo'lsa, operator bir oynada sig'gan matnni ikkinchisida qayta
 * yozishga majbur bo'lardi.
 */
export const VOUCHER_REASON_MAX = 200

/**
 * BITTA kartani bekor qiladi: `NEW`/`SOLD` → `VOIDED` (`subscriber.delete`).
 *
 * ⚠️ Sabab bu yerda MAJBURIY (backendda `required = false`). Sabab audit
 * yozuvidagi yagona izoh: bekor qilingan karta hisobotda qoladi va "bu
 * nega kuydi?" savoliga javob faqat shu maydondan chiqadi.
 *
 * Karta `ACTIVATED`/`EXPIRED`/`VOIDED` bo'lsa backend **409** beradi
 * (`VOUCHER_VOID_<HOLAT>`), begona partiyaniki bo'lsa — **404** (403 emas:
 * u "bunday karta bor" degan ma'lumotni oshkor qilardi).
 */
export async function voidVoucher(id: string, reason: string): Promise<Voucher> {
  const response = await api.post<unknown>(`/api/vouchers/${id}/void`, undefined, {
    params: { reason },
  })
  return toVoucher(voucherSchema.parse(response.data))
}

export const ACTIVATION_CHANNELS = ['STAFF', 'PORTAL', 'PORTAL_ACCOUNT', 'SELFCARE'] as const
export type ActivationChannel = (typeof ACTIVATION_CHANNELS)[number]

export const ENTRY_METHODS = ['CODE', 'QR_SCAN', 'QR_IMAGE', 'QR_LINK'] as const
export type EntryMethod = (typeof ENTRY_METHODS)[number]

const voucherActivationSchema = z.object({
  voucherId: uuidSchema,
  code: z.string(),
  batchNo: z.number(),
  planId: uuidSchema,
  subscriberLogin: z.string().nullish(),
  activatedAt: instantSchema,
  expiresAt: instantSchema.nullish(),
  channel: z.string().nullish(),
  entryMethod: z.string().nullish(),
  branchName: z.string().nullish(),
  nasIdentifier: z.string().nullish(),
  clientMac: z.string().nullish(),
  clientIp: z.string().nullish(),
  venueId: uuidSchema.nullish(),
  zoneId: uuidSchema.nullish(),
  replacedGrants: z.number().nullish(),
  activatedByName: z.string().nullish(),
})

export interface VoucherActivation {
  voucherId: string
  code: string
  batchNo: number
  planId: string
  subscriberLogin: string | null
  activatedAt: string
  expiresAt: string | null
  channel: string | null
  entryMethod: string | null
  branchName: string | null
  nasIdentifier: string | null
  clientMac: string | null
  clientIp: string | null
  venueId: string | null
  zoneId: string | null
  replacedGrants: number | null
  activatedByName: string | null
}

function toActivation(raw: z.infer<typeof voucherActivationSchema>): VoucherActivation {
  return {
    voucherId: raw.voucherId,
    code: raw.code,
    batchNo: raw.batchNo,
    planId: raw.planId,
    subscriberLogin: raw.subscriberLogin ?? null,
    activatedAt: raw.activatedAt,
    expiresAt: raw.expiresAt ?? null,
    channel: raw.channel ?? null,
    entryMethod: raw.entryMethod ?? null,
    branchName: raw.branchName ?? null,
    nasIdentifier: raw.nasIdentifier ?? null,
    clientMac: raw.clientMac ?? null,
    clientIp: raw.clientIp ?? null,
    venueId: raw.venueId ?? null,
    zoneId: raw.zoneId ?? null,
    replacedGrants: raw.replacedGrants ?? null,
    activatedByName: raw.activatedByName ?? null,
  }
}

const ACTIVATION_FILTERS = ['from', 'to', 'nodeId', 'planId', 'nas', 'channel', 'batchNo'] as const

export function activationListParams(state: TableState): Record<string, unknown> {
  const params = listParams(state, { sortFields: {} })
  for (const name of ACTIVATION_FILTERS) {
    const value = state.filters[name]?.[0]?.trim()
    if (value) params[name] = value
  }
  return params
}

export async function fetchVoucherActivationPage(
  state: TableState,
  signal: AbortSignal,
): Promise<PageResult<VoucherActivation>> {
  const response = await api.get<unknown>('/api/vouchers/activations', {
    signal,
    params: activationListParams(state),
  })
  const parsed = serverPageSchema(voucherActivationSchema).parse(response.data)
  return toPageResult(parsed, parsed.rows.map(toActivation))
}

// --- O'tkazmalar, reyestr va VISP hisoboti (ADR-056) ----------------------------

const voucherTransferSchema = z.object({
  id: uuidSchema,
  transferNo: z.string(),
  batchId: uuidSchema,
  batchNo: z.number(),
  planId: uuidSchema,
  fromHolderId: uuidSchema.nullish(),
  fromHolderName: z.string().nullish(),
  toHolderId: uuidSchema.nullish(),
  toHolderName: z.string().nullish(),
  cards: z.number(),
  note: z.string().nullish(),
  createdByName: z.string().nullish(),
  createdAt: instantSchema,
})

export interface VoucherTransfer {
  id: string
  /** `VT-2026-000123` */
  transferNo: string
  batchId: string
  batchNo: number
  planId: string
  /** `null` — partiya operatorda edi */
  fromHolderName: string | null
  toHolderId: string | null
  toHolderName: string | null
  /** O'tkazma paytidagi kartalar soni */
  cards: number
  note: string | null
  createdByName: string | null
  createdAt: string
}

export interface VoucherTransferFilter {
  search?: string
  batchId?: string
  toHolderId?: string
  limit?: number
}

/** Eng yangisi birinchi */
export async function fetchVoucherTransfers(
  filter: VoucherTransferFilter,
  signal?: AbortSignal,
): Promise<VoucherTransfer[]> {
  const params: Record<string, string | number> = {}
  if (filter.search?.trim()) params['search'] = filter.search.trim()
  if (filter.batchId) params['batchId'] = filter.batchId
  if (filter.toHolderId) params['toHolderId'] = filter.toHolderId
  if (filter.limit) params['limit'] = filter.limit
  const response = await api.get<unknown>('/api/vouchers/transfers', { params, ...(signal ? { signal } : {}) })
  return z
    .array(voucherTransferSchema)
    .parse(response.data)
    .map((raw) => ({
      id: raw.id,
      transferNo: raw.transferNo,
      batchId: raw.batchId,
      batchNo: raw.batchNo,
      planId: raw.planId,
      fromHolderName: raw.fromHolderName ?? null,
      toHolderId: raw.toHolderId ?? null,
      toHolderName: raw.toHolderName ?? null,
      cards: raw.cards,
      note: raw.note ?? null,
      createdByName: raw.createdByName ?? null,
      createdAt: raw.createdAt,
    }))
}

/**
 * Kartaning hisoblangan holati (ADR-056 §4): Kutilmoqda = `NEW`/`SOLD`,
 * Faol/Ishlatilgan — `ACTIVATED` paket amal qilishiga qarab, `EXPIRED` —
 * sotilmasdan muddati o'tgan.
 */
export const CARD_STATUSES = ['PENDING', 'ACTIVE', 'USED', 'CANCELLED', 'EXPIRED'] as const
export type CardStatus = (typeof CARD_STATUSES)[number]

const registerRowSchema = z.object({
  voucherId: uuidSchema,
  batchNo: z.number(),
  serialNo: z.number(),
  code: z.string(),
  vispId: uuidSchema.nullish(),
  vispName: z.string().nullish(),
  planId: uuidSchema,
  status: z.enum(CARD_STATUSES),
  activatedAt: instantSchema.nullish(),
  expiresAt: instantSchema.nullish(),
  subscriberLogin: z.string().nullish(),
  voidedAt: instantSchema.nullish(),
  voidReason: z.string().nullish(),
})

export interface VoucherRegisterRow {
  voucherId: string
  batchNo: number
  serialNo: number
  /** Karta kodi, login va parolli kartada — login */
  code: string
  vispName: string | null
  planId: string
  status: CardStatus
  activatedAt: string | null
  expiresAt: string | null
  subscriberLogin: string | null
  voidedAt: string | null
  voidReason: string | null
}

const REGISTER_FILTERS = ['vispId', 'planId', 'batchNo', 'serial', 'status', 'from', 'to'] as const

export async function fetchVoucherRegisterPage(
  state: TableState,
  signal: AbortSignal,
): Promise<PageResult<VoucherRegisterRow>> {
  const params = listParams(state, { sortFields: {} })
  for (const name of REGISTER_FILTERS) {
    const value = state.filters[name]?.[0]?.trim()
    if (value) params[name] = value
  }
  const response = await api.get<unknown>('/api/vouchers/register', { signal, params })
  const parsed = serverPageSchema(registerRowSchema).parse(response.data)
  return toPageResult(
    parsed,
    parsed.rows.map((raw) => ({
      voucherId: raw.voucherId,
      batchNo: raw.batchNo,
      serialNo: raw.serialNo,
      code: raw.code,
      vispName: raw.vispName ?? null,
      planId: raw.planId,
      status: raw.status,
      activatedAt: raw.activatedAt ?? null,
      expiresAt: raw.expiresAt ?? null,
      subscriberLogin: raw.subscriberLogin ?? null,
      voidedAt: raw.voidedAt ?? null,
      voidReason: raw.voidReason ?? null,
    })),
  )
}

const vispReportRowSchema = z.object({
  vispId: uuidSchema.nullish(),
  vispName: z.string().nullish(),
  pending: z.number(),
  active: z.number(),
  used: z.number(),
  cancelled: z.number(),
  expired: z.number(),
  total: z.number(),
})

export interface VoucherVispReportRow {
  /** `null` — VISPsiz partiyalar */
  vispId: string | null
  vispName: string | null
  pending: number
  active: number
  used: number
  cancelled: number
  expired: number
  total: number
}

export interface VoucherReportFilter {
  planId: string | null
  batchNo: string | null
  from: string | null
  to: string | null
}

const REPORT_FILTERS = ['planId', 'batchNo', 'from', 'to'] as const satisfies readonly (keyof VoucherReportFilter)[]

export function voucherReportParams(filter: VoucherReportFilter): Record<string, string> {
  const params: Record<string, string> = {}
  for (const name of REPORT_FILTERS) {
    const value = filter[name]
    if (value) params[name] = value
  }
  return params
}

export async function fetchVoucherVispReport(
  filter: VoucherReportFilter,
  signal?: AbortSignal,
): Promise<VoucherVispReportRow[]> {
  const response = await api.get<unknown>('/api/vouchers/report/visp', {
    params: voucherReportParams(filter),
    ...(signal ? { signal } : {}),
  })
  return z
    .array(vispReportRowSchema)
    .parse(response.data)
    .map((raw) => ({ ...raw, vispId: raw.vispId ?? null, vispName: raw.vispName ?? null }))
}
